import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { checkDeviceRevoked } from '../lib/devices';
import { setSentryUser } from '../lib/sentry';
import { initIap, logoutIap } from '../lib/iap';

/**
 * SessionProvider — single source of truth for the user's auth state.
 *
 * BEFORE this provider existed, every component that called useSession() (in
 * `lib/auth.ts`) was setting up its OWN supabase.auth.onAuthStateChange
 * listener AND its own useState. When a sign-in fired, every instance
 * independently called setSession(), which cascaded re-renders through the
 * tree and caused RequireSubscription's redirect effect to fire 100+ times
 * per second (Sentry HS-SAAS-APP-A and the "flickering" report 11 Jun 2026).
 *
 * Now: ONE listener, ONE state setter, ONE context consumed by every
 * useSession() call. The hook's public shape — { session, loading } — is
 * unchanged, so callers don't need to be rewritten.
 *
 * Mount this provider once at the very top of the tree (above the Router,
 * above App). See main.tsx.
 */

interface SessionContextValue {
  session: Session | null;
  loading: boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
      // Tag Sentry reports with the user id so we can match an incoming
      // error to a real customer immediately. No PII (email/name) — the
      // id alone is enough for our lookup needs.
      setSentryUser(data.session?.user?.id ?? null);
      // Configure RevenueCat with the current user id on native shells.
      // No-op on web / when there's no session. Idempotent, so cheap to
      // call every time the session ref changes.
      if (data.session?.user?.id) void initIap(data.session.user.id);
      if (data.session) void enforceDeviceRevocation();
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (cancelled) return;
      setSession(s);
      setSentryUser(s?.user?.id ?? null);
      if (s?.user?.id) {
        void initIap(s.user.id);
      } else if (event === 'SIGNED_OUT') {
        void logoutIap();
      }
      if (s) void enforceDeviceRevocation();
    });

    // Re-check device revocation on tab focus — covers the
    // "I revoked you from my phone five minutes ago" case.
    const onFocus = () => { void enforceDeviceRevocation(); };
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return (
    <SessionContext.Provider value={{ session, loading }}>
      {children}
    </SessionContext.Provider>
  );
}

/**
 * Hook used everywhere in the app. Same shape as the old `useSession` so
 * existing call sites work unchanged. Throws if used outside the provider
 * so a future regression is loud rather than silently regressing back to
 * the multi-listener storm.
 */
export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used inside a <SessionProvider>');
  }
  return ctx;
}

/** Sign-out and device-revoke helper. Pulled out so the provider stays
 *  focused on session-state and doesn't import the device cache. */
async function enforceDeviceRevocation(): Promise<void> {
  try {
    const status = await checkDeviceRevoked();
    if (status.revoked) {
      const reason = status.revokedReason;
      const msg =
        reason === 'cap_exceeded'
          ? "You've signed in from too many devices. The oldest one (this one) has been signed out. You can sign back in any time."
          : reason === 'user_revoke'
          ? 'This device was signed out from your account. Sign in again to continue.'
          : 'This device has been signed out. Sign in again to continue.';
      try { window.sessionStorage.setItem('sas_device_revoked_msg', msg); } catch { /* noop */ }
      await supabase.auth.signOut();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
  } catch {
    // Network or RPC issue — don't sign the user out for that
  }
}
